import { base } from './primitives.ts';

/**
 * Semantic color tokens (alias layer) — reference primitives by USAGE, never by hue.
 * Mirrored from Figma Variables (collection "colours"). Components and the theme consume
 * these, not the primitives directly.
 *
 * Groups: text · surface · intent · state.
 * Tier rules live in CLAUDE.md / docs/DECISIONS.md (theme-first, token-first).
 */

/** Foreground / content. Neutral only — colored text comes from `intent.*.text`. */
export const text = {
  primary: base.grey080,
  secondary: base.grey070,
  disabled: base.grey030,
  white: base.white,
  link: base.purple,
} as const;

/** Surfaces / backgrounds. `overlay` is an alpha scrim; `inverse` backs inverse content. */
export const surface = {
  default: base.grey004,
  raised: base.grey002,
  sunken: base.grey006,
  /** Pure white surface — used where a card/table must be brighter than the raised grey
   *  paper (Card, SelectionCard, TableContainer). Distinct from `raised` (grey002). */
  white: base.white,
  // Modal scrim — aligned with MUI's Backdrop default (50% black) 2026-07-21 and wired to the
  // Dialog/Drawer backdrops in theme/components.ts. Mirror in Figma: overlay alpha 30% → 50%.
  overlay: 'rgba(0, 0, 0, 0.5)',
  inverse: base.midnightBlue,
} as const;

/**
 * Intents — brand + feedback. Each carries a fixed facet set so one intent builds a filled
 * control, a subtle background, colored text and a border. `disabled` is a fixed faded fill
 * per intent (project decision); disabled text uses `text.disabled`.
 *
 * Maps 1:1 onto MUI palette slots: primary, secondary, info, success, error, warning.
 * `neutral` is a custom slot (no native MUI equivalent) and omits `text` — neutral text
 * comes from the `text.*` group.
 *
 * `border` is a SOFT OUTLINE (the `*040` tints, 2026-08-11) — a resting/selected boundary, e.g. a
 * selected chip or card edge. At ~1.5–2.1:1 against a light surface it is deliberately quiet and
 * therefore NOT a state signal. Anything that has to be *seen* — a keyboard focus ring, an active
 * tab indicator — must use `solid` instead (~6.9:1 for primary), or it silently fails the 3:1
 * non-text contrast bar. `neutral.border` (grey010) is unchanged. See docs/DECISIONS.md.
 */
export const intent = {
  primary: {
    solid: base.purple,
    onSolid: base.white,
    subtle: base.purple008,
    text: base.purple,
    border: base.purple040,
    disabled: base.purple016,
  },
  secondary: {
    solid: base.midnightBlue,
    onSolid: base.white,
    subtle: base.midnightBlue008,
    text: base.midnightBlue,
    border: base.midnightBlue040,
    disabled: base.midnightBlue016,
  },
  neutral: {
    solid: base.grey016,
    onSolid: base.grey080,
    subtle: base.grey006,
    border: base.grey010,
    disabled: base.grey006,
  },
  info: {
    solid: base.denim,
    onSolid: base.white,
    subtle: base.denim008,
    text: base.denim,
    border: base.denim040,
    disabled: base.denim016,
  },
  success: {
    solid: base.green116,
    onSolid: base.white,
    subtle: base.green008,
    text: base.green120,
    border: base.green040,
    disabled: base.green016,
  },
  error: {
    solid: base.red,
    onSolid: base.white,
    subtle: base.red008,
    text: base.red120,
    border: base.red040,
    disabled: base.red016,
  },
  warning: {
    solid: base.yellow,
    // NOTE (a11y, tracked): white on Yellow is low-contrast (~1.8:1). Left per design
    // decision for now — revisit with a darker onSolid (e.g. text.primary) later.
    onSolid: base.white,
    subtle: base.yellow008,
    text: base.yellow120,
    border: base.yellow040,
    disabled: base.yellow016,
  },
} as const;

/**
 * Alpha values behind the `state` overlays — the single source for BOTH the rgba strings
 * below and the numeric `palette.action.*Opacity` slots in theme.ts (plus e.g. the Chip
 * disabled-decoration opacity). Change an alpha here and every consumer follows.
 */
export const stateAlpha = {
  hover: 0.04,
  selected: 0.08,
  pressed: 0.12,
  disabledContent: 0.38,
  disabledSurface: 0.1,
  disabledBorder: 0.12,
} as const;

/**
 * Interaction states — translucent overlays composited over whatever is beneath, so one
 * value works on any surface. `disabled.surface`/`border`/`content` are the flat-neutral
 * disabled (used sparingly; intents carry their own `disabled` fill).
 */
export const state = {
  hover: `rgba(0, 0, 0, ${stateAlpha.hover})`,
  selected: `rgba(0, 0, 0, ${stateAlpha.selected})`,
  pressed: `rgba(0, 0, 0, ${stateAlpha.pressed})`,
  disabled: {
    content: `rgba(0, 0, 0, ${stateAlpha.disabledContent})`,
    surface: `rgba(0, 0, 0, ${stateAlpha.disabledSurface})`,
    border: `rgba(0, 0, 0, ${stateAlpha.disabledBorder})`,
  },
} as const;

export type TextTokens = typeof text;
export type SurfaceTokens = typeof surface;
export type IntentTokens = typeof intent;
export type StateTokens = typeof state;
export type StateAlphaTokens = typeof stateAlpha;
